import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, 
  Loader2,
  Search,
  Filter,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye
} from 'lucide-react';

const AdminUsersPage = ({ filterStatus }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    role: searchParams.get('role') || '',
    status: filterStatus || searchParams.get('status') || '',
  });

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;
      
      const response = await api.get('/admin/users', { params });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Pendiente', icon: Clock },
      in_review: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'En Revisión', icon: Clock },
      approved: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Aprobado', icon: CheckCircle2 },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado', icon: XCircle },
      more_info_required: { className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', label: 'Info Requerida', icon: AlertCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={style.className}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Super Admin' },
      fund_director: { className: 'bg-purple-500/10 text-purple-500 border-purple-500/20', label: 'Director' },
      analyst: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Analista' },
      compliance: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Cumplimiento' },
      investor: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Inversionista' },
      property_contributor: { className: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20', label: 'Contribuidor' },
    };
    const style = styles[role] || styles.investor;
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-8" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">
            {filterStatus === 'in_review' ? 'Verificaciones Pendientes' : 'Gestión de Usuarios'}
          </h1>
          <p className="body-text">
            {filterStatus === 'in_review' 
              ? 'Revise y apruebe las solicitudes de verificación KYC.'
              : `${total} usuarios registrados en la plataforma.`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            {!filterStatus && (
              <>
                <Select
                  value={filters.role}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, role: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Todos los roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los roles</SelectItem>
                    <SelectItem value="investor">Inversionistas</SelectItem>
                    <SelectItem value="property_contributor">Contribuidores</SelectItem>
                    <SelectItem value="analyst">Analistas</SelectItem>
                    <SelectItem value="fund_director">Directores</SelectItem>
                    <SelectItem value="compliance">Cumplimiento</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.status}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="in_review">En Revisión</SelectItem>
                    <SelectItem value="approved">Aprobado</SelectItem>
                    <SelectItem value="rejected">Rechazado</SelectItem>
                    <SelectItem value="more_info_required">Info Requerida</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : users.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <p className="text-muted-foreground">No se encontraron usuarios</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <Card key={u.user_id} className="card-dashboard hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                        {u.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getRoleBadge(u.role)}
                      {getStatusBadge(u.verification_status)}
                    </div>
                    
                    <div className="text-right text-xs text-muted-foreground hidden md:block">
                      <p>Registro: {new Date(u.created_at).toLocaleDateString('es-MX')}</p>
                      <p>Perfil: {u.profile_completion || 0}%</p>
                    </div>
                    
                    <Link to={`/admin/users/${u.user_id}`}>
                      <Button variant="outline" size="sm" className="gap-2">
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                        Ver
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
